import { GLOBAL } from '@lumapps/translations';

import { PostType } from './types';

export enum POSTS {
    /**
     * @value Articles offer additional formatting options and seamless insertion of media throughout your text.
     */
    ARTICLE_INFO = 'POSTS.ARTICLE_INFO',
    /**
     * @value Attach link preview
     */
    ATTACHMENTS_ADD_LINKS = 'FRONT.ATTACHMENTS.ADD_LINKS',
    /**
     * @value You can add either attachments or link previews in posts.
     * @description The text in the tooltip when hovering the "Add attachments" button while a link is already inserted. Or hovering the "Add link" button while there is already attachments.
     */
    ATTACHMENTS_OR_LINK_WARNING = 'FRONT.ATTACHMENTS.ATTACHMENTS_OR_LINK_WARNING',
    /**
     * @value Back to post
     * @description Text of a button used to redirect the user to the post.
     * `Post` is the type of the content, not the action here
     */
    BACK_TO_POST = 'POSTS.BACK_TO_POST',
    /**
     * @value Add a status
     */
    COMMUNITY_STATUS_ADD = 'COMMUNITY_STATUS_ADD',
    /**
     * @value with status
     */
    COMMUNITY_STATUS_PREFIX = 'COMMUNITY_STATUS_PREFIX',
    /**
     * @value Add a category
     */
    COMMUNITY_TAG_ADD = 'COMMUNITY_TAG_ADD',
    /**
     * @value By discarding the post, <strong>the attached article will be deleted.</strong>
     * @description When clicking the cancel button of a post dialog with a newly created article, a dialog is displayed to warn the user his article will be deleted.
     * This key is the body text of the said warning dialog.
     */
    CONFIRM_DIALOG_BODY_DELETE_ARTICLE = 'FRONT.COMMUNITY.POST.CONFIRM_DIALOG.BODY.DELETE_ARTICLE',
    /**
     * @value By discarding the post, <strong>the attached event will be deleted.</strong>
     */
    CONFIRM_DIALOG_BODY_DELETE_EVENT = 'FRONT.COMMUNITY.POST.CONFIRM_DIALOG.BODY.DELETE_EVENT',
    /**
     * @value Discard post and delete article
     * @description When clicking the cancel button of a post dialog with a newly created article, a dialog is displayed to warn the user his article will be deleted.
     * This key is the confirmation button label of the said warning dialog.
     */
    CONFIRM_DIALOG_BUTTONS_DELETE_ARTICLE = 'FRONT.COMMUNITY.POST.CONFIRM_DIALOG.BUTTONS.DELETE_ARTICLE',
    /**
     * @value Discard post and delete event
     */
    CONFIRM_DIALOG_BUTTONS_DELETE_EVENT = 'FRONT.COMMUNITY.POST.CONFIRM_DIALOG.BUTTONS.DELETE_EVENT',
    /**
     * @value Discard post and delete article?
     * @description When clicking the cancel button of a post dialog with a newly created article, a dialog is displayed to warn the user his article will be deleted.
     * This key is the title of the said warning dialog.
     */
    CONFIRM_DIALOG_TITLE_DELETE_ARTICLE = 'FRONT.COMMUNITY.POST.CONFIRM_DIALOG.TITLE.DELETE_ARTICLE',
    /**
     * @value Discard post and delete event ?
     */
    CONFIRM_DIALOG_TITLE_DELETE_EVENT = 'FRONT.COMMUNITY.POST.CONFIRM_DIALOG.TITLE.DELETE_EVENT',
    /**
     * @value Continue working on your publication as an article or switch back to a post?
     * @description Text message informating user about his post to article conversion.
     */
    CONVERSION_MESSAGE = 'POSTS.CONVERSION_MESSAGE',
    /**
     * @value Convert to article
     * @description Button that confirm the convertion of a post to an article
     */
    CONVERT_TO_ARTICLE = 'POSTS.CONVERT_TO_ARTICLE',
    /**
     * @value Create a post
     * @description In community pages there is a Fab button to create a new post. This is the label of the main Floating Fab button that leads to the creation of a "standard" post.
     */
    CREATE_A_POST = 'FRONT.COMMUNITY.POST.CREATE.TYPE.DEFAULT',
    /**
     * @value Custom date
     * @description To schedule a post
     */
    CUSTOM_DATE = 'POSTS.CUSTOM_DATE',
    /**
     * @value Remove all attachments
     */
    DELETE_ALL = 'FRONT.ATTACHMENTS.DELETE_ALL',
    /**
     * @value Are you sure you want to delete this post?
     * @description The description message of the confirm dialog when you want to delete a post.
     */
    DELETE_CONFIRM_DESCRIPTION = 'COMMUNITY_POST.DELETE_CONFIRM_DIALOG_DESCRIPTION',
    /**
     * @value Delete post
     * @description The title of the confirm dialog when you want to delete a post.
     */
    DELETE_CONFIRM_TITLE = 'COMMUNITY_POST.DELETE_CONFIRM_DIALOG_TITLE',
    /**
     * @value Upload to your post
     * @description Text used on a drag and drop zone allowing the user to drag and drop a file in a post to add it as attachment
     */
    DIALOG_DROP_UPLOAD = 'POSTS.DIALOG_DROP_UPLOAD',
    /**
     * @value Downvote
     */
    DOWNVOTE = 'POST_DOWNVOTE',
    /**
     * @value Edit event
     */
    EDIT_EVENT = 'FRONT.EVENT.DIALOG.EDIT_EVENT',
    /**
     * @value Edit idea
     */
    EDIT_IDEA = 'COMMUNITY_POST.EDIT_IDEA',
    /**
     * @value Edit post
     * @description The label of the button opening the post edition dialog
     */
    EDIT_POST = 'COMMUNITY_POST.EDIT_ACTION',
    /**
     * @value Edit question
     */
    EDIT_QUESTION = 'COMMUNITY_POST.EDIT_QUESTION',
    /**
     * @value Edit scheduled date
     * @description Label on a button to define a different date for a content publication
     */
    EDIT_SCHEDULED_DATE = 'POSTS.EDIT_SCHEDULED_DATE',
    /**
     * @value Enter video URL
     * @description Text used for example to label a button inviting the user to enter the url of a video (youtube, dailymotion etc...) in order to integrate it in a post.
     */
    ENTER_URL = 'FRONT.ENTER_VIDEO_URL',
    /**
     * @value From
     */
    EVENT_FROM_DATE = 'COMMUNITY_POST_EVENT_FROM_DATE',
    /**
     * @value To
     */
    EVENT_TO_DATE = 'COMMUNITY_POST_EVENT_TO_DATE',
    /**
     * @value Monday at 10:00 AM
     */
    MONDAY_AT_10AM = 'POSTS.MONDAY_AT_10AM',
    /**
     * @value New event
     */
    NEW_EVENT = 'FRONT.EVENT.DIALOG.NEW_EVENT',
    /**
     * @value New idea
     */
    NEW_IDEA = 'FRONT.CONTRIBUTION_MENU.NEW_IDEA',
    /**
     * @value New post
     * @description title of the first section in the contribution menu
     */
    NEW_POST = 'FRONT.CONTRIBUTION_MENU.NEW_POST',
    /**
     * @value New question
     */
    NEW_QUESTION = 'FRONT.CONTRIBUTION_MENU.NEW_QUESTION',
    /**
     * @value Post type
     * @description Dropdown allowing to change the type of a post
     */
    OPEN_TYPE_OPTIONS = 'OPEN_TYPE_OPTIONS',
    /**
     * @value {"one":"%NUMBER% Downvote","other":"%NUMBER% Downvotes"}
     * @description Vote results
     */
    PLURALIZED_DOWNVOTES = 'POST_PLURALIZED_DOWNVOTES',
    /**
     * @value {"one":"%NUMBER% Upvote","other":"%NUMBER% Upvotes"}
     * @description Vote result
     */
    PLURALIZED_UPVOTES = 'POST_PLURALIZED_UPVOTES',
    /**
     * @value Post analytics
     * @description Label for a button to open an analytics dashboard
     */
    POST_ANALYTICS = 'POSTS.POST_ANALYTICS',
    /**
     * @value Schedule an event
     * @description In community pages there is a Floating button to create a new post. This is the label of one of the Floating Fab button that leads to the creation of an "event" post.
     * (this fab button with a calendar icon appears when the user hovers over the main fab button on the bottom right of the screen)
     */
    POST_CREATE_TYPE_EVENT = 'FRONT.COMMUNITY.POST.CREATE.TYPE.EVENT',
    /**
     * @value Suggest an idea
     * @description In community pages there is a Floating button to create a new post. This is the label of one of the Floating Fab button that leads to the creation of an "idea" post.
     * (this fab button with a light bulb icon appears when the user hovers over the main fab button on the bottom right of the screen)
     */
    POST_CREATE_TYPE_IDEA = 'FRONT.COMMUNITY.POST.CREATE.TYPE.IDEA',
    /**
     * @value Ask a question
     * @description In community pages there is a Floating button to create a new post. This is the label of one of the Floating Fab button that leads to the creation of a "question" post.
     * (this fab button with a question mark icon appears when the user hovers over the main fab button on the bottom right of the screen)
     */
    POST_CREATE_TYPE_QUESTION = 'FRONT.COMMUNITY.POST.CREATE.TYPE.QUESTION',
    /**
     * @value Post deleted successfully
     */
    POST_DELETE_SUCCESS = 'COMMUNITY_POST_DELETE_SUCCESS',
    /**
     * @value Post description
     */
    POST_DESCRIPTION = 'POST_DESCRIPTION',
    /**
     * @value Post is saved as draft
     * @description "Post" as a publication.
     */
    POST_IS_SAVED_AS_DRAFT = 'POSTS.POST_IS_SAVED_AS_DRAFT',
    /**
     * @value Post is scheduled
     * @description Post as a publication that is planned to go live later
     */
    POST_IS_SCHEDULED = 'POSTS.POST_IS_SCHEDULED',
    /**
     * @value The post has been moved successfully
     */
    POST_MOVE_SUCCESS = 'POST_MOVE_SUCCESS',
    /**
     * @value Your post has been published.
     */
    POST_PUBLISHED = 'FRONT.COMMUNITY.NEW_POST.PUBLISHED',
    /**
     * @value Post status
     */
    POST_STATUS = 'POST_STATUS',
    /**
     * @value Default
     */
    POST_TYPE = 'COMMUNITY_POST_TYPE_DEFAULT',
    /**
     * @value Event
     */
    POST_TYPE_EVENT = 'COMMUNITY_POST_TYPE_EVENT',
    /**
     * @value Idea
     */
    POST_TYPE_IDEA = 'COMMUNITY_POST_TYPE_IDEA',
    /**
     * @value Question
     */
    POST_TYPE_QUESTION = 'COMMUNITY_POST_TYPE_QUESTION',
    /**
     * @value Your post is over 1300 characters. To make it easier to read and engage your audience, try converting it into an article.
     * @description Message displayed on a post to inform a user that he can convert his post to an article. `Post` concern the type of content here not the action
     */
    PROPOSAL_CONVERT_TO_ARTICLE = 'POSTS.PROPOSAL_CONVERT_TO_ARTICLE',
    /**
     * @value Schedule post
     * @description Define a time where the post will be published
     */
    SCHEDULE_POST = 'POSTS.SCHEDULE_POST',
    /**
     * @value Tomorrow at 10:00 AM
     */
    TOMORROW_AT_10AM = 'POSTS.TOMORROW_AT_10AM',
    /**
     * @value Translate into %LANG%
     * @description The label of the button to translate a content (can be a comment, post, or a page). Takes the target language as a variable.
     */
    TRANSLATE_ACTION = 'FRONT.AUTO_TRANSLATION.TRANSLATE_ACTION',
    /**
     * @value Upvote
     */
    UPVOTE = 'POST_UPVOTE',
    /**
     * @value View post
     * @description Label on a button to redirect to the "post" page
     */
    VIEW_POST = 'POSTS.VIEW_POST',
    /**
     * @value vote
     */
    VOTE = 'VOTE',
    /**
     * @value votes
     */
    VOTES = 'VOTES',
    /**
     * @value You haven’t finished your post yet. Are you sure you want to leave and discard your draft?
     * @description Displayed when user creates a post, writes something, then tries to Cancel post creation.
     */
    WARNING_UNFINISHED_EDIT = 'FRONT.COMMUNITY.POST.WARNING.UNFINISHED_EDIT',
    /**
     * @value with status
     */
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    WITH_STATUS = 'COMMUNITY_STATUS_PREFIX',
}

export const getCommunityPostType = (type: string) => `COMMUNITY_POST_TYPE_${type}`;

export const POST_TITLE_PLACEHOLDER_BY_TYPE: { [key in PostType]: string } = {
    [PostType.DEFAULT]: GLOBAL.TITLE,
    [PostType.IDEA]: GLOBAL.TITLE,
    [PostType.QUESTION]: GLOBAL.TITLE,
    [PostType.EVENT]: 'FRONT.COMMUNITY.POST.TITLE_PLACEHOLDER.EVENT',
};
